<script setup>
import {computed, ref, watch} from "vue";
import Select2 from "vue3-select2-component";

import useHelpers from "@/Composables/UseHelpers";

const {$t} = useHelpers();

const emit = defineEmits([
  'update:exclude_tax',
  'update:tax_ic_no',
  'update:tax_address1',
  'update:tax_address2',
  'update:tax_postcode',
  'update:tax_city',
  'update:tax_state',
  'update:tax_country',
  'select',
]);

const exclude_tax_model = ref('')
const tax_ic_no_model = ref('')
const tax_state_model = ref('')
const tax_city_model = ref('')
const tax_country_model = ref('')
const tax_postcode_model = ref('')

const props = defineProps({
  tenant_can_issue_tax_receipt: {
    required: true,
  },
  exclude_tax: {
    required: true,
    default: 0
  },
  tax_ic_no: {
    required: true,
  },
  tax_address1: {
    required: true,
  },
  tax_address2: {
    required: true,
  },
  tax_postcode: {
    required: true,
  },
  tax_city: {
    required: true,
  },
  tax_state: {
    required: true,
  },
  tax_country: {
    required: true,
  },
  city_opts: {
    required: true,
  },
  state_opts: {
    required: true,
  },
  country_opts: {
    required: true,
  },
  default_country: {
    required: true,
  },
  errors: {
    required: true,
  }
});

const select2Config = ref({
  width: '100%',
  default: props.default_country
});

watch(() => tax_ic_no_model.value, (val) => {
  tax_ic_no_model.value = val.replace(/\D/g, '')
});

watch(() => tax_postcode_model.value, (val) => {
  tax_postcode_model.value = val.replace(/\D/g, '')
});

const filteredTaxCityOpts = computed(() => {
  return props.city_opts[tax_state_model.value] ?? []
});
</script>

<template>
  <template v-if="tenant_can_issue_tax_receipt">
    <div class="border-top pt-4 mt-3">
      <h2>
        {{ $t('donation-form.tax_exemption') }}
      </h2>

      <!-- Receive tax exemption? -->
      <div class="form-group row">
        <label class="col-4 col-form-label d-flex align-items-center pt-2" for="exclude_tax">
          {{ $t('donation-form.received_receipt_tax') }}
        </label>
        <div class="col-8 d-flex align-items-center pb-1">
          <div class="custom-control custom-switch">
            <input
                type="checkbox"
                name="exclude_tax"
                id="exclude_tax"
                class="custom-control-input"
                :checked="exclude_tax_model == 1"
                v-model="exclude_tax_model"
                @input="emit('update:exclude_tax', $event.target.checked)"
            />

            <label class="custom-control-label" for="exclude_tax"></label>
          </div>
        </div>
      </div>

      <!-- Tax Details -->
      <template v-if="exclude_tax">

        <!-- I/C Number -->
        <div class="form-group row mb-2">
          <label class="col-4 col-form-label" for="tax_ic_no">
            {{ $t('donation-form.ic_no') }}

            <!-- Required -->
            <span class="text-red"> *</span>
          </label>
          <div class="col-8">
            <input
                type="text"
                inputmode="numeric"
                id="tax_ic_no"
                name="tax_ic_no"
                maxlength="12"
                required="required"
                class="form-control"
                :disabled=" ! exclude_tax"
                :class="{ 'is-invalid': errors.tax_ic_no }"
                v-model="tax_ic_no_model"
                @input="emit('update:tax_ic_no', $event.target.value)"
            >

            <!-- Error message -->
            <div class="invalid-feedback" v-if="errors.tax_ic_no">
              {{ errors.tax_ic_no }}
            </div>
          </div>
        </div>

        <!-- Address Line 1 -->
        <div class="form-group row mb-2">
          <label class="col-4 col-form-label" for="tax_address1">
            {{ $t('donation-form.address') }}

            <!-- Required -->
            <span class="text-red"> *</span>
          </label>
          <div class="col-8">
            <input
                type="text"
                id="tax_address1"
                name="tax_address1"
                required="required"
                class="form-control"
                :disabled=" ! exclude_tax"
                :class="{ 'is-invalid': errors.tax_address1 }"
                :value="tax_address1"
                @input="emit('update:tax_address1', $event.target.value)"
            >

            <!-- Error message -->
            <div class="invalid-feedback" v-if="errors.tax_address1">
              {{ errors.tax_address1 }}
            </div>
          </div>
        </div>

        <!-- Address Line 2 -->
        <div class="form-group row mb-2">
          <label class="col-4 col-form-label" for="tax_address2"></label>
          <div class="col-8">
            <input
                type="text"
                id="tax_address2"
                name="tax_address2"
                required="required"
                class="form-control"
                :disabled=" ! exclude_tax"
                :value="tax_address2"
                @input="emit('update:tax_address2', $event.target.value)"
            >
          </div>
        </div>

        <!-- Postcode -->
        <div class="form-group row mb-2">
          <label class="col-4 col-form-label" for="tax_postcode">
            {{ $t('donation-form.postcode') }}

            <!-- Required -->
            <span class="text-red"> *</span>
          </label>
          <div class="col-8">
            <input
                type="text"
                id="tax_postcode"
                inputmode="numeric"
                maxlength="5"
                name="tax_postcode"
                :class="{ 'form-control': true, 'is-invalid': errors.tax_postcode }"
                required="required"
                :disabled=" ! exclude_tax"
                v-model="tax_postcode_model"
                @input="emit('update:tax_postcode', $event.target.value)"
            >

            <!-- Error message -->
            <div class="invalid-feedback" v-if="errors.tax_postcode">
              {{ errors.tax_postcode }}
            </div>
          </div>
        </div>

        <!-- State -->
        <div class="form-group row mb-2">
          <label class="col-4 col-form-label" for="tax_state">
            {{ $t('donation-form.state') }}

            <!-- Required -->
            <span class="text-red"> *</span>
          </label>
          <div class="col-8">
            <Select2
                :class="{ 'is-invalid': Boolean(errors.tax_state) }"
                :placeholder="$t('donation-form.select_state')"
                :options="Object.values(state_opts)"
                :settings="select2Config"
                v-model="tax_state_model"
                @select="emit('update:tax_state', tax_state_model)"
            />

            <!-- Error message -->
            <div class="invalid-feedback" v-if="errors.tax_state">
              {{ errors.tax_state }}
            </div>
          </div>
        </div>

        <!-- City -->
        <div class="form-group row mb-2">
          <label class="col-4 col-form-label" for="tax_city">
            {{ $t('donation-form.city') }}

            <!-- Required -->
            <span class="text-red"> *</span>
          </label>
          <div class="col-8">
            <Select2
                :class="{ 'is-invalid': Boolean(errors.tax_city) }"
                :placeholder="$t('donation-form.select_city')"
                :options="filteredTaxCityOpts"
                :settings="select2Config"
                v-model="tax_city_model"
                @select="emit('update:tax_city', tax_city_model)"
            />

            <!-- Error message -->
            <div class="invalid-feedback" v-if="errors.tax_city">
              {{ errors.tax_city }}
            </div>
          </div>
        </div>

        <!-- Country -->
        <div class="form-group row mb-2">
          <label class="col-4 col-form-label" for="tax_country">
            {{ $t('donation-form.country') }}

            <!-- Required -->
            <span class="text-red"> *</span>
          </label>
          <div class="col-8">
            <Select2
                :class="{ 'is-invalid': Boolean(errors.tax_country) }"
                :placeholder="$t('donation-form.select_country')"
                :options="Object.values(country_opts)"
                :settings="select2Config"
                v-model="tax_country_model"
                @select="emit('update:tax_country', tax_country_model)"
            />

            <!-- Error message -->
            <div class="invalid-feedback" v-if="errors.tax_country">
              {{ errors.tax_country }}
            </div>
          </div>
        </div>
      </template>
    </div>
  </template>
</template>